// Demo mode axios adapter — intercepts requests when token is 'demo-token'
// and returns mock data from demoData.ts without hitting the network.
import type { InternalAxiosRequestConfig } from 'axios';
import type { PlaylistItem } from '../types';
import { demoData } from './demoData';

interface MockResponse {
  data: unknown;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: InternalAxiosRequestConfig;
}

function respond(config: InternalAxiosRequestConfig, data: unknown, status = 200): MockResponse {
  return { data, status, statusText: 'OK', headers: { 'content-type': 'application/json' }, config };
}

function respondBlob(config: InternalAxiosRequestConfig, blob: Blob): MockResponse {
  return { data: blob, status: 200, statusText: 'OK', headers: { 'content-type': 'image/svg+xml' }, config };
}

// Extract ID from URL patterns like /cameras/cam-1 or /cameras/cam-1/live-stats
function extractId(url: string, prefix: string): string {
  const after = url.split(prefix)[1] || '';
  return after.split('/')[0].split('?')[0];
}

// Parse query params from URL
function parseParams(url: string): Record<string, string> {
  const q = url.split('?')[1];
  if (!q) return {};
  return Object.fromEntries(new URLSearchParams(q));
}

// Parse JSON body from config
function parseBody(config: InternalAxiosRequestConfig): Record<string, unknown> {
  if (!config.data) return {};
  if (typeof config.data === 'string') {
    try { return JSON.parse(config.data); } catch { return {}; }
  }
  return config.data as Record<string, unknown>;
}

// Small delay to simulate network latency
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export async function demoAdapter(config: InternalAxiosRequestConfig): Promise<MockResponse> {
  await delay(50 + Math.random() * 150); // 50-200ms fake latency

  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();
  const params = { ...parseParams(url), ...(config.params || {}) };
  const body = parseBody(config);

  // ─── Auth ────────────────────────────────────────────
  if (url.includes('/auth/refresh')) {
    return respond(config, {
      access_token: 'demo-token', refresh_token: 'demo-refresh',
      user: { id: 'demo-user', email: 'demo@dominate.io', role: 'admin', organization_id: 'demo-org', created_at: new Date().toISOString() },
    });
  }

  // ─── Organizations ───────────────────────────────────
  if (url.match(/\/organizations\/[^/]+\/settings/) && method === 'patch') {
    return respond(config, demoData.organizations.updateSettings('', body as Partial<Record<string, unknown>> as any));
  }
  if (url.match(/\/organizations\/[^/]+/) && method === 'get') {
    return respond(config, demoData.organizations.get());
  }
  if (url.match(/\/organizations\/[^/]+/) && method === 'put') {
    return respond(config, demoData.organizations.update('', body.name as string));
  }
  if (url.endsWith('/organizations') && method === 'get') {
    return respond(config, demoData.organizations.list());
  }
  if (url.endsWith('/organizations') && method === 'post') {
    return respond(config, demoData.organizations.create(body.name as string));
  }

  // ─── Branches ────────────────────────────────────────
  if (url.match(/\/branches\/[^/]+/) && method === 'put') {
    const id = extractId(url, '/branches/');
    return respond(config, demoData.branches.update(id, body as any));
  }
  if (url.match(/\/branches\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/branches/');
    demoData.branches.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/branches\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/branches/');
    return respond(config, demoData.branches.get(id));
  }
  if (url.endsWith('/branches') && method === 'post') {
    return respond(config, demoData.branches.create(body as any));
  }
  if (url.endsWith('/branches') || url.includes('/branches?')) {
    return respond(config, demoData.branches.list());
  }

  // ─── Cameras (specific routes first) ─────────────────
  if (url.includes('/cameras/') && url.endsWith('/live-stats')) {
    return respond(config, demoData.cameras.getLiveStats());
  }
  if (url.includes('/cameras/') && url.endsWith('/settings-history')) {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.getSettingsHistory(id));
  }
  if (url.includes('/cameras/') && url.endsWith('/settings') && method === 'patch') {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.updateSettings(id, body as any));
  }
  if (url.includes('/cameras/') && url.endsWith('/regenerate-key')) {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.regenerateApiKey(id));
  }
  if (url.includes('/cameras/') && url.endsWith('/move')) {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.move(id));
  }
  if (url.includes('/cameras/') && url.endsWith('/snapshot')) {
    const id = extractId(url, '/cameras/');
    return respondBlob(config, demoData.cameras.getSnapshot(id));
  }
  if (url.includes('/cameras/') && url.includes('/snapshot/status')) {
    return respond(config, demoData.cameras.getSnapshotStatus());
  }
  if (url.includes('/cameras/') && url.endsWith('/request-snapshot')) {
    return respond(config, null);
  }
  // Zone routes nested under cameras
  if (url.includes('/cameras/') && url.endsWith('/zones/counts/latest')) {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.zones.getLatestCounts(id));
  }
  if (url.includes('/cameras/') && url.endsWith('/zones') && method === 'post') {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.zones.create(id, body));
  }
  if (url.includes('/cameras/') && url.endsWith('/zones') && method === 'get') {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.zones.listForCamera(id));
  }
  // Camera CRUD
  if (url.match(/\/cameras\/[^/]+/) && method === 'put') {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.update(id, body as any));
  }
  if (url.match(/\/cameras\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/cameras/');
    demoData.cameras.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/cameras\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/cameras/');
    return respond(config, demoData.cameras.get(id));
  }
  if ((url.endsWith('/cameras') || url.includes('/cameras?')) && method === 'post') {
    return respond(config, demoData.cameras.register(body.name as string, body.location as string, body.branch_id as string));
  }
  if (url.endsWith('/cameras') || url.includes('/cameras?')) {
    return respond(config, demoData.cameras.list());
  }

  // ─── Analytics ───────────────────────────────────────
  if (url.includes('/analytics/summary')) {
    return respond(config, demoData.analytics.getSummary(params.period || '7d'));
  }
  if (url.includes('/analytics/timeseries')) {
    return respond(config, demoData.analytics.getTimeseries(params.period || '7d', parseInt(params.interval || '60')));
  }
  if (url.includes('/analytics/engagement')) {
    return respond(config, demoData.analytics.getEngagement());
  }
  if (url.includes('/analytics/traffic-patterns')) {
    return respond(config, demoData.analytics.getTrafficPatterns());
  }
  if (url.includes('/analytics/cross-demographics')) {
    return respond(config, demoData.analytics.getCrossDemographics());
  }
  if (url.includes('/analytics/video-stats')) {
    return respond(config, demoData.analytics.getVideoStats());
  }
  if (url.includes('/analytics/live-feed')) {
    return respond(config, demoData.analytics.getLiveFeed(parseInt(params.limit || '50')));
  }
  if (url.includes('/analytics/zones/summary')) {
    return respond(config, demoData.zones.getAnalyticsSummary());
  }
  if (url.includes('/analytics/clear')) {
    return respond(config, demoData.analytics.clearData());
  }

  // ─── Faces ───────────────────────────────────────────
  if (url.match(/\/faces\/[^/]+/) && method === 'patch') {
    const id = extractId(url, '/faces/');
    return respond(config, demoData.faces.update(id, body as any));
  }
  if (url.match(/\/faces\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/faces/');
    demoData.faces.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/faces\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/faces/');
    return respond(config, demoData.faces.get(id));
  }
  if (url.endsWith('/faces') || url.includes('/faces?')) {
    return respond(config, demoData.faces.list());
  }

  // ─── Zones ───────────────────────────────────────────
  if (url.match(/\/zones\/[^/]+\/counts/) && method === 'get') {
    return respond(config, demoData.zones.getCountHistory());
  }
  if (url.match(/\/zones\/[^/]+\/product-recognition/) && method === 'patch') {
    demoData.products.updateZoneProductRecognition();
    return respond(config, null);
  }
  if (url.match(/\/zones\/[^/]+\/products/) && method === 'get') {
    return respond(config, demoData.products.getZoneSightings());
  }
  if (url.match(/\/zones\/[^/]+\/video/) && method === 'get') {
    const id = extractId(url, '/zones/');
    return respond(config, demoData.zoneVideo.getMapping(id));
  }
  if (url.match(/\/zones\/[^/]+\/video/) && method === 'put') {
    const id = extractId(url, '/zones/');
    return respond(config, demoData.zoneVideo.setMapping(id, body.video_id as string));
  }
  if (url.match(/\/zones\/[^/]+\/video/) && method === 'delete') {
    demoData.zoneVideo.removeMapping();
    return respond(config, null, 204);
  }
  if (url.match(/\/zones\/[^/]+/) && method === 'put') {
    const id = extractId(url, '/zones/');
    return respond(config, demoData.zones.update(id, body));
  }
  if (url.match(/\/zones\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/zones/');
    demoData.zones.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/zones\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/zones/');
    return respond(config, demoData.zones.get(id));
  }
  if (url.endsWith('/zones') || url.includes('/zones?')) {
    return respond(config, demoData.zones.list());
  }

  // ─── Products ────────────────────────────────────────
  if (url.match(/\/products\/[^/]+/) && method === 'patch') {
    const id = extractId(url, '/products/');
    return respond(config, demoData.products.update(id, body));
  }
  if (url.match(/\/products\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/products/');
    demoData.products.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/products\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/products/');
    return respond(config, demoData.products.get(id));
  }
  if (url.endsWith('/products') && method === 'delete') {
    return respond(config, demoData.products.clearAll());
  }
  if (url.endsWith('/products') && method === 'post') {
    return respond(config, demoData.products.create(body));
  }
  if (url.endsWith('/products') || url.includes('/products?')) {
    return respond(config, demoData.products.list(params as any));
  }

  // ─── Videos ──────────────────────────────────────────
  if (url.includes('/videos/upload') && method === 'post') {
    // Extract filename from FormData if possible
    const formData = config.data as FormData | undefined;
    const file = formData?.get?.('file') as File | null;
    return respond(config, demoData.videos.upload(file?.name || 'uploaded-video.mp4'));
  }
  if (url.match(/\/videos\/[^/]+\/demographics/) && method === 'patch') {
    const id = extractId(url, '/videos/');
    return respond(config, demoData.videos.updateDemographics(id, body));
  }
  if (url.match(/\/videos\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/videos/');
    demoData.videos.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/videos\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/videos/');
    return respond(config, demoData.videos.get(id));
  }
  if (url.endsWith('/videos') || url.includes('/videos?')) {
    return respond(config, demoData.videos.list());
  }

  // ─── Media Players ──────────────────────────────────
  if (url.includes('/media-players/') && url.endsWith('/regenerate-code')) {
    return respond(config, demoData.mediaPlayers.regenerateCode());
  }
  if (url.includes('/media-players/') && url.endsWith('/playlist') && method === 'put') {
    const id = extractId(url, '/media-players/');
    return respond(config, demoData.mediaPlayers.updatePlaylist(id, (body.items || []) as PlaylistItem[]));
  }
  if (url.includes('/media-players/') && url.endsWith('/playlist') && method === 'get') {
    const id = extractId(url, '/media-players/');
    return respond(config, demoData.mediaPlayers.getPlaylist(id));
  }
  if (url.match(/\/media-players\/[^/]+/) && method === 'put') {
    const id = extractId(url, '/media-players/');
    return respond(config, demoData.mediaPlayers.update(id, body as any));
  }
  if (url.match(/\/media-players\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/media-players/');
    demoData.mediaPlayers.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/media-players\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/media-players/');
    return respond(config, demoData.mediaPlayers.get(id));
  }
  if (url.endsWith('/media-players') && method === 'post') {
    return respond(config, demoData.mediaPlayers.create(body.name as string, body.camera_id as string, body.branch_id as string));
  }
  if (url.endsWith('/media-players') || url.includes('/media-players?')) {
    return respond(config, demoData.mediaPlayers.list());
  }

  // ─── Users ───────────────────────────────────────────
  if (url.match(/\/users\/[^/]+/) && method === 'put') {
    const id = extractId(url, '/users/');
    return respond(config, demoData.users.update(id, body as any));
  }
  if (url.match(/\/users\/[^/]+/) && method === 'delete') {
    const id = extractId(url, '/users/');
    demoData.users.delete(id);
    return respond(config, null, 204);
  }
  if (url.match(/\/users\/[^/]+/) && method === 'get') {
    const id = extractId(url, '/users/');
    return respond(config, demoData.users.get(id));
  }
  if (url.endsWith('/users') || url.includes('/users?')) {
    return respond(config, demoData.users.list());
  }

  // ─── Alerts ──────────────────────────────────────────
  if (url.includes('/alerts/mark-all-read')) {
    return respond(config, demoData.alerts.markAllRead());
  }
  if (url.match(/\/alerts\/\d+\/read/)) {
    demoData.alerts.markRead();
    return respond(config, null);
  }
  if (url.endsWith('/alerts') || url.includes('/alerts?')) {
    return respond(config, demoData.alerts.list());
  }

  // ─── Subscription / Billing ─────────────────────────
  if (url.includes('/subscription/status')) {
    return respond(config, {
      is_valid: true, status: 'active', days_until_expiry: 22,
      grace_period_active: false, camera_count: 9, total_amount: '776.25',
    });
  }
  if (url.includes('/subscription/trial') && method === 'post') {
    const trialDays = Number(body.days) || 30;
    return respond(config, {
      message: `Trial granted for ${trialDays} days`,
      subscription: {
        id: 'sub-trial', organization_id: body.organization_id || 'demo-org', status: 'active',
        camera_count: 0, price_per_camera: '86.25', total_amount: '0.00', currency: 'ZAR',
        current_period_start: new Date().toISOString().split('T')[0],
        current_period_end: new Date(Date.now() + trialDays * 86400000).toISOString().split('T')[0],
        next_billing_date: new Date(Date.now() + trialDays * 86400000).toISOString().split('T')[0],
        trial_start: new Date().toISOString().split('T')[0],
        trial_end: (body.trial_end_date as string) || new Date(Date.now() + trialDays * 86400000).toISOString().split('T')[0],
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      },
    });
  }
  if (url.endsWith('/subscription') && method === 'get') {
    return respond(config, {
      subscription: {
        id: 'sub-demo', organization_id: 'demo-org', status: 'active',
        camera_count: 9, price_per_camera: '86.25', total_amount: '776.25',
        currency: 'ZAR',
        current_period_start: new Date(Date.now() - 8 * 86400000).toISOString().split('T')[0],
        current_period_end: new Date(Date.now() + 22 * 86400000).toISOString().split('T')[0],
        next_billing_date: new Date(Date.now() + 22 * 86400000).toISOString().split('T')[0],
        trial_start: new Date(Date.now() - 8 * 86400000).toISOString().split('T')[0],
        trial_end: new Date(Date.now() + 22 * 86400000).toISOString().split('T')[0],
        created_at: new Date(Date.now() - 90 * 86400000).toISOString(),
        updated_at: new Date().toISOString(),
      },
      camera_count: 9, price_per_camera: '86.25', price_per_camera_excl_vat: '75.00',
    });
  }
  if (url.includes('/payfast/payment/generate') && method === 'post') {
    return respond(config, { payment_url: '#demo', payment_data: {} });
  }
  if (url.includes('/payfast/payment/history')) {
    return respond(config, {
      payments: [
        { id: 'pay-1', organization_id: 'demo-org', amount: '776.25', currency: 'ZAR', status: 'completed', camera_count_at_payment: 9, billing_period_start: new Date(Date.now() - 38 * 86400000).toISOString().split('T')[0], billing_period_end: new Date(Date.now() - 8 * 86400000).toISOString().split('T')[0], created_at: new Date(Date.now() - 38 * 86400000).toISOString(), completed_at: new Date(Date.now() - 38 * 86400000).toISOString(), payfast_transaction_id: 'PF-DEMO-001' },
        { id: 'pay-2', organization_id: 'demo-org', amount: '690.00', currency: 'ZAR', status: 'completed', camera_count_at_payment: 8, billing_period_start: new Date(Date.now() - 68 * 86400000).toISOString().split('T')[0], billing_period_end: new Date(Date.now() - 38 * 86400000).toISOString().split('T')[0], created_at: new Date(Date.now() - 68 * 86400000).toISOString(), completed_at: new Date(Date.now() - 68 * 86400000).toISOString(), payfast_transaction_id: 'PF-DEMO-002' },
      ],
    });
  }
  if (url.includes('/payfast/subscription/cancel') && method === 'post') {
    return respond(config, { message: 'Subscription cancelled', payfast_cancelled: true, access_until: new Date(Date.now() + 22 * 86400000).toISOString() });
  }

  // ─── Admin ────────────────────────────────────────────
  if (url.endsWith('/admin/dashboard') && method === 'get') {
    return respond(config, {
      total_customers: 12, total_cameras: 47, active_subscriptions: 10, monthly_revenue: '4057.50',
      subscription_breakdown: { active: 10, grace_period: 1, expired: 0, cancelled: 1 },
    });
  }
  if (url.match(/\/admin\/customers\/[^/]+\/suspend/) && method === 'post') {
    return respond(config, { message: 'Organization suspended' });
  }
  if (url.match(/\/admin\/customers\/[^/]+\/unsuspend/) && method === 'post') {
    return respond(config, { message: 'Organization unsuspended' });
  }
  if (url.match(/\/admin\/customers\/[^/]+\/impersonate/) && method === 'post') {
    return respond(config, { access_token: 'demo-token', user: { id: 'demo-user', email: 'demo@peoplewatcher.io', role: 'user', organization_id: 'demo-org' }, organization: 'Demo Organization', expires_in: 3600 });
  }
  if (url.match(/\/admin\/customers\/[^/]+/) && method === 'get') {
    return respond(config, { organization: demoData.organizations.get(), owner_email: 'demo@peoplewatcher.io', camera_count: 9, subscription: null });
  }
  if (url.endsWith('/admin/customers') && method === 'get') {
    return respond(config, [
      { organization: { ...demoData.organizations.get(), name: 'Demo Organization' }, owner_email: 'demo@peoplewatcher.io', camera_count: 9, subscription: { id: 'sub-1', status: 'active', total_amount: '776.25' } },
      { organization: { ...demoData.organizations.get(), id: 'org-2', name: 'Acme Retail', is_suspended: false }, owner_email: 'admin@acme.co.za', camera_count: 5, subscription: { id: 'sub-2', status: 'active', total_amount: '431.25' } },
      { organization: { ...demoData.organizations.get(), id: 'org-3', name: 'ShopSmart Holdings', is_suspended: true, suspended_at: new Date(Date.now() - 5 * 86400000).toISOString(), suspension_reason: 'Non-payment' }, owner_email: 'ops@shopsmart.co.za', camera_count: 3, subscription: { id: 'sub-3', status: 'suspended', total_amount: '258.75' } },
      { organization: { ...demoData.organizations.get(), id: 'org-4', name: 'FreshMart', is_suspended: false }, owner_email: 'info@freshmart.co.za', camera_count: 2, subscription: { id: 'sub-4', status: 'active', total_amount: '0.00', trial_start: new Date(Date.now() - 5 * 86400000).toISOString().split('T')[0], trial_end: new Date(Date.now() + 25 * 86400000).toISOString().split('T')[0] } },
    ]);
  }

  // ─── Fallback ────────────────────────────────────────
  console.warn('[Demo Mode] Unhandled route:', method.toUpperCase(), url);
  return respond(config, null, 404);
}
